import React, { useMemo } from 'react'
import { addDays, format, isBefore } from '@e3dc-react/shell/Libs/date'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { ChargingSessionModel } from '../../../charging-sessions/models/charging-sessions.model'
import { DashboardChart } from './DashboardChart'
import { makeStyles } from 'tss-react/mui'
import { Box, Paper } from '@mui/material'
import { WallboxType } from '../../../wallboxes/models/wallbox-model'
import { DashboardFilterAction, DashboardState, MetricType } from '../../models/dashboard.model'
import { useTypedSelector } from 'src/State/RootReducer'
import { ErrorSkeleton } from '@e3dc-react/shell/Elements/Error'

const useStyles = makeStyles()(theme => ({
  chartContainer: {
    padding: theme.spacing(3),
    [theme.breakpoints.down('lg')]: {
      padding: theme.spacing(2),
    },
    flex: 1,
    minHeight: 275,
  },
  errorSkeleton: {
    height: '100%',
    /**
     * Error Skeleton is not 'fully' responsive(it offers two heights, one "small" and one "large" essentially.
     * The layout of e-mobility page shows chart inside a large container that takes up the majority of the page
     * so the ErrorSkeleton does not really expand to fit the whole container properly.
     *
     * For this reason, we(just for this e-mobility dashboard layout) override the positioning of the label
     * "No data available" to keep it perfectly centered inside this large chart area, BUT keep the bars stuck
     * to the bottom of the container(as real chart bars would be stuck to the bottom, and not centered vertically)
     */
    '& > .MuiBox-root': {
      height: '100%',
      '& > .MuiGrid-container': {
        marginTop: 0,
      },
    },
  },
}))

interface DashboardChartContainerProps {
  chargingSessions: ChargingSessionModel[]
  unfilteredData: ChargingSessionModel[]
  dispatch: React.Dispatch<DashboardFilterAction>
  dashboardState: DashboardState
  currencyCode: string
}

interface ValuesSumByDate {
  grid: number
  solar: number
  discharged: number
  gridCost: number
  solarCost: number
  dischargeCost: number
  date: Date
}

const MIN_CHART_VALUE = 0.005

/**
 * Chart container component holds chart as a parent component statistics chart for emobility dashboard chart.
 * It also aggregates data for chart
 * @param - chargingSessions as ChargingSessionModel array where we are extracting first created session as starting point for date range
 * @param - unfiltered data as ChargingSessionModel array
 * @param - dashboard reducer dispatch actions
 * @param - dashboardState from reducer where user can't select date before first session ever created
 */
export const DashboardChartContainer: React.FC<DashboardChartContainerProps> = ({
  chargingSessions,
  unfilteredData,
  dispatch,
  dashboardState,
  currencyCode,
}) => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()

  const dashboardMetric = useTypedSelector(state => state.eMobility.dashboard.metric)

  const isEdisonConnect = unfilteredData.some(wallbox => wallbox.wallbox.type === WallboxType.Bidi)

  const chartData = useMemo(() => {
    /** first, filter for sessions with actual energy data */
    const chargingSessionsWithRelevantValues = chargingSessions.filter(session => {
      switch (dashboardMetric) {
        case MetricType.Energy:
          return session.gridEnergyKWh > 0 || session.energySolarKWh > 0 || session.energyDischargedKWh > 0
        case MetricType.Cost:
          return session.gridCost > 0 || session.solarCost > 0 || session.totalDischargeCost > 0
        default:
          return false
      }
    })

    /** then, remove "time" extension from datetime string to condense sessions of the same day, regardless of exact time */
    const chartDataByDate =
      chargingSessionsWithRelevantValues?.map(session => {
        /** temporarily store date in common YYYY-MM-DD format for easier parsing, localization will happen inside chart tick formatter and tooltip formatter */

        let formattedDate = format(new Date(session.startDateTime), 'yyyy-MM-dd')

        if (dashboardState.filters.dataRange.view === 'year') {
          formattedDate = format(new Date(session.startDateTime), 'yyyy-MM')
        }

        return {
          date: formattedDate,
          grid: session.gridEnergyKWh,
          solar: session.energySolarKWh,
          discharged: session.energyDischargedKWh,
          gridCost: session.gridCost,
          solarCost: session.solarCost,
          dischargeCost: session.totalDischargeCost,
        }
      }) || []

    const valueSumByDate: Record<string, ValuesSumByDate> = {}

    /** if date entry already exists, add energy to existing sum for that specific day, otherwise create new date entry */
    chartDataByDate.forEach(item => {
      if (!valueSumByDate[item.date]) {
        valueSumByDate[item.date] = {
          grid: 0,
          solar: 0,
          discharged: 0,
          gridCost: 0,
          solarCost: 0,
          dischargeCost: 0,
          date: new Date(item.date),
        }
      }

      valueSumByDate[item.date].grid += item.grid
      valueSumByDate[item.date].solar += item.solar
      valueSumByDate[item.date].discharged += item.discharged
      valueSumByDate[item.date].gridCost += item.gridCost
      valueSumByDate[item.date].solarCost += item.solarCost
      valueSumByDate[item.date].dischargeCost += item.dischargeCost
    })

    /** fill with empty data for missing dates between min and max date, e.g. to plot all 31 days of March when "current month" option is selected */
    for (
      let m = new Date(dashboardState.filters.dataRange.startDate);
      isBefore(m, new Date(dashboardState.filters.dataRange.endDate));
      m = addDays(m, 1)
    ) {
      let date = format(m, 'yyyy-MM-dd')

      if (dashboardState.filters.dataRange.view === 'year') {
        date = format(m, 'yyyy-MM')
      }

      if (!valueSumByDate[date]) {
        valueSumByDate[date] = {
          grid: 0,
          solar: 0,
          discharged: 0,
          gridCost: 0,
          solarCost: 0,
          dischargeCost: 0,
          date: new Date(date),
        }
      }
    }

    return Object.values(valueSumByDate)
      .sort((a, b) => (isBefore(a.date, b.date) ? -1 : 1))
      .map(item => ({
        /*
         * We round and display values with 2 decimal digits
         * Values under 0.005 are displayed in chart bar but when we round it for dynamic chart legend value is 0.00
         * We are displaying 0 for values under 0.005 for both chart bar and dynamic legend to stay consistence
         */
        grid: Math.max(Math.abs(item.grid) < MIN_CHART_VALUE ? 0 : item.grid, 0),
        solar: Math.max(Math.abs(item.solar) < MIN_CHART_VALUE ? 0 : item.solar, 0),
        discharged: Math.abs(item.discharged) < MIN_CHART_VALUE ? 0 : -Math.abs(item.discharged),
        gridCost: Math.abs(item.gridCost) < MIN_CHART_VALUE ? 0 : item.gridCost,
        solarCost: Math.abs(item.solarCost) < MIN_CHART_VALUE ? 0 : item.solarCost,
        dischargeCost: Math.abs(item.dischargeCost) < MIN_CHART_VALUE ? 0 : -Math.abs(item.dischargeCost),
        date:
          dashboardState.filters.dataRange.view === 'year'
            ? format(item.date, t('common.format.date-fns-date-without-day'))
            : format(item.date, t('common.format.date-fns-date')),
      }))
  }, [chargingSessions, dashboardState.filters.dataRange, dashboardMetric, t])

  return (
    <Paper id="e-mobility-dashboard-container" className={classes.chartContainer}>
      {chargingSessions.length === 0 ? (
        <Box className={classes.errorSkeleton}>
          <ErrorSkeleton title={t('common.noData')} type="barChart" />
        </Box>
      ) : (
        <DashboardChart metric={dashboardMetric} data={chartData} isEdisonConnect={isEdisonConnect} currencyCode={currencyCode} />
      )}
    </Paper>
  )
}
